import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { externalSubscriptionService } from '@/services/externalSubscriptionService';
import { fetchStripeAccountId } from '@/services/transactions/stripeAccountService';

interface ItemCounts {
  products: number;
  offers: number;
  subscriptions: number;
  transactions: number;
}

interface ItemCountsContextType {
  counts: ItemCounts;
  loading: boolean;
  hasAnyItems: boolean;
  hasProducts: boolean;
  hasOffers: boolean;
  hasSubscriptions: boolean;
  hasTransactions: boolean;
  refreshCounts: () => Promise<void>;
  fetchStripeCounts: () => Promise<void>; // Separate function for Stripe counts only
}

const ItemCountsContext = createContext<ItemCountsContextType | undefined>(undefined);

export const ItemCountsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [counts, setCounts] = useState<ItemCounts>({
    products: 0,
    offers: 0,
    subscriptions: 0,
    transactions: 0
  });
  const [loading, setLoading] = useState(true);
  const hasFetchedRef = useRef(false);
  const isFetchingRef = useRef(false);

  // Fetch only database counts (products and offers) - no edge functions
  const fetchDatabaseCounts = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      // Fetch database counts only (no edge functions)
      const [productsResult, offersResult] = await Promise.all([
        supabase.from('products').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('offer_pages').select('id', { count: 'exact', head: true }).eq('user_id', user.id)
      ]);

      setCounts(prev => ({
        ...prev,
        products: productsResult.count || 0,
        offers: offersResult.count || 0,
      }));
    } catch (error) {
      console.error('Error fetching database counts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Stripe-related counts (subscriptions and transactions) - only when needed
  const fetchStripeCounts = useCallback(async () => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      return;
    }
    
    isFetchingRef.current = true;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        isFetchingRef.current = false;
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.warn('ItemCountsContext: No session found, skipping edge function calls');
        isFetchingRef.current = false;
        return;
      }

      let subscriptionCount = 0;
      let transactionCount = 0;

      const connectedAccountId = await fetchStripeAccountId();
      
      if (connectedAccountId) {
        try {
          const subscriptionData = await externalSubscriptionService.fetchAllSubscriptions({
            connectedAccountId,
            limit: 1000, 
            statusFilter: 'all'
          });

          if (subscriptionData?.subscriptions) {
            subscriptionCount = subscriptionData.statistics.total || subscriptionData.subscriptions.length;
          }
        } catch (error) {
          console.warn('Error fetching subscription count from edge function:', error);
        }
      } else {
        console.log('ItemCountsContext: No connected account ID found, skipping subscription count');
      }

      try {
        const { data: transactionData } = await supabase.functions.invoke('get-transactions', {
          body: { limit: 1000 }, 
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          }
        });

        if (transactionData?.transactions) {
          transactionCount = transactionData.transactions.length;
        }
      } catch (error) {
        console.warn('Error fetching transaction count from edge function:', error);
      }

      setCounts(prev => ({
        ...prev,
        subscriptions: subscriptionCount,
        transactions: transactionCount
      }));
      hasFetchedRef.current = true;
    } catch (error) {
      console.error('Error fetching Stripe counts:', error);
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  // Combined function for backward compatibility
  const fetchCounts = useCallback(async () => {
    await fetchDatabaseCounts();
    await fetchStripeCounts();
  }, [fetchDatabaseCounts, fetchStripeCounts]);

  // Fetch database counts (products/offers) on mount - no edge functions needed
  useEffect(() => {
    fetchDatabaseCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only fetch DB counts on mount

  // Don't fetch Stripe counts automatically - only when user visits specific tabs
  // This avoids unnecessary edge function calls at app startup

  // Listen for product/offer created events
  useEffect(() => {
    const handleProductCreated = () => {
      console.log('ItemCountsContext: Product created event received, refreshing counts');
      setTimeout(fetchCounts, 100); 
    };

    const handleOfferCreated = () => {
      console.log('ItemCountsContext: Offer created event received, refreshing counts');
      setTimeout(fetchCounts, 100);
    };

    globalThis.addEventListener('product-created', handleProductCreated);
    globalThis.addEventListener('offer-created', handleOfferCreated);

    return () => {
      globalThis.removeEventListener('product-created', handleProductCreated);
      globalThis.removeEventListener('offer-created', handleOfferCreated);
    };
  }, [fetchCounts]);

  const hasAnyItems = counts.products > 0 || counts.offers > 0 || counts.subscriptions > 0 || counts.transactions > 0;

  return (
    <ItemCountsContext.Provider
      value={{
        counts,
        loading,
        hasAnyItems,
        hasProducts: counts.products > 0,
        hasOffers: counts.offers > 0,
        hasSubscriptions: counts.subscriptions > 0,
        hasTransactions: counts.transactions > 0,
        refreshCounts: fetchCounts,
        fetchStripeCounts
      }}
    >
      {children}
    </ItemCountsContext.Provider>
  );
};

export const useItemCountsContext = () => {
  const context = useContext(ItemCountsContext);
  if (context === undefined) {
    throw new Error('useItemCountsContext must be used within an ItemCountsProvider');
  }
  return context;
};
